import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToastContext } from '../shared/context/ToastContext';
import { useAuth } from '../shared/context/AuthContext';
import './CampaignList.css';
import { apiFetch } from '../shared/api';

function CampaignList() {
  const { success, error: showError } = useToastContext();
  const { isAdmin } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCampaigns(campaigns);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = campaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(query) ||
        (campaign.brand && campaign.brand.toLowerCase().includes(query)) ||
        (campaign.client_name && campaign.client_name.toLowerCase().includes(query))
      );
      setFilteredCampaigns(filtered);
    }
  }, [searchQuery, campaigns]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/campaigns');
      if (!response.ok) throw new Error('Failed to fetch campaigns');
      const data = await response.json();
      setCampaigns(data);
      setFilteredCampaigns(data);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const response = await apiFetch(`/campaigns/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete campaign');
      await fetchCampaigns();
      success('Campaign deleted successfully');
    } catch (err) {
      showError(err.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading campaigns...</div>;
  }

  return (
    <div className="campaign-list">
      <div className="campaign-list-header">
        <div>
          <h2>Campaigns</h2>
          <p style={{ color: 'var(--color-muted-foreground)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
            {filteredCampaigns.length} {filteredCampaigns.length === 1 ? 'campaign' : 'campaigns'}
            {searchQuery && ` found`}
          </p>
        </div>
        <Link to="/campaigns/new" className="btn btn-primary">
          + Create New Campaign
        </Link>
      </div>

      {campaigns.length > 0 && (
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search campaigns by name, brand, or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="empty-state">
          <h3>No campaigns yet</h3>
          <p>Create your first campaign to get started</p>
          <Link to="/campaigns/new" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Create Campaign
          </Link>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="empty-state">
          <h3>No campaigns found</h3>
          <p>Try adjusting your search query</p>
          <button
            onClick={() => setSearchQuery('')}
            className="btn btn-secondary"
            style={{ marginTop: '1rem' }}
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="campaigns-grid">
          {filteredCampaigns.map(campaign => (
            <div key={campaign.id} className="campaign-card">
              <div className="campaign-card-header">
                <h3>{campaign.name}</h3>
                <div className="campaign-card-actions">
                  <Link to={`/campaigns/${campaign.id}`} className="btn btn-primary btn-sm">
                    View
                  </Link>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(campaign.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="campaign-card-body">
                <p><strong>Description:</strong> {campaign.description || 'Not set'}</p>
                <p><strong>Brand:</strong> {campaign.brand || 'Not set'}</p>
                {isAdmin && (
                  <p>
                    <strong>Client:</strong>{' '}
                    <span className="campaign-client-pill">{campaign.client_name || 'Unassigned'}</span>
                  </p>
                )}
                <p><strong>Created:</strong> {new Date(campaign.created_at).toLocaleDateString()}</p>
              </div>
              <div className="campaign-card-footer">
                <div className="feed-url-display">
                  <small style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-muted-foreground)' }}>
                    Feed URL:
                  </small>
                  <div className="feed-url-with-copy">
                    <code>{campaign.feed_url || ''}</code>
                    <button
                      onClick={() => {
                        const feedUrl = campaign.feed_url || '';
                        navigator.clipboard.writeText(feedUrl);
                        success('Feed URL copied to clipboard');
                      }}
                      className="btn-copy"
                      title="Copy URL"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CampaignList;

